if (!customElements.get('hero-video-carousel')) {
  customElements.define(
    'hero-video-carousel',
    class HeroVideoCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.currentIndex = 0;
        this.pointerStartX = 0;
        this.pointerStartY = 0;
        this.pointerActive = false;
        this.lastWheelAt = 0;
        this.wheelDelta = 0;
        this.suppressClick = false;
        this.pausedByEditor = false;

        this.stage = this.querySelector('[data-hero-video-carousel-stage]');
        this.slides = Array.from(this.querySelectorAll('[data-hero-video-carousel-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-hero-video-carousel-dot]'));
        this.previousButton = this.querySelector('[data-hero-video-carousel-previous]');
        this.nextButton = this.querySelector('[data-hero-video-carousel-next]');
        this.interval = Math.max(Number(this.dataset.interval) || 5000, 1000);
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        if (!this.slides.length) return;

        const activeSlideIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
        this.currentIndex = activeSlideIndex > -1 ? activeSlideIndex : 0;

        this.onPreviousClick = this.previous.bind(this);
        this.onNextClick = this.next.bind(this);
        this.onDotClick = this.handleDotClick.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onPointerCancel = this.handlePointerCancel.bind(this);
        this.onWheel = this.handleWheel.bind(this);
        this.onStageClick = this.handleStageClick.bind(this);
        this.onVisibilityChange = this.handleVisibilityChange.bind(this);
        this.onReducedMotionChange = this.handleReducedMotionChange.bind(this);
        this.onShopifyBlockSelect = this.handleShopifyBlockSelect.bind(this);
        this.onShopifyBlockDeselect = this.handleShopifyBlockDeselect.bind(this);

        this.bindEvents();
        this.prepareVideos();
        this.update();
        this.playActiveVideo();
        this.startAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.unbindEvents();
        this.pauseAllVideos();
      }

      bindEvents() {
        this.previousButton?.addEventListener('click', this.onPreviousClick);
        this.nextButton?.addEventListener('click', this.onNextClick);
        this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
        this.stage?.addEventListener('keydown', this.onKeydown);
        this.stage?.addEventListener('pointerdown', this.onPointerDown);
        this.stage?.addEventListener('pointerup', this.onPointerUp);
        this.stage?.addEventListener('pointercancel', this.onPointerCancel);
        this.stage?.addEventListener('wheel', this.onWheel, { passive: false });
        this.stage?.addEventListener('click', this.onStageClick, true);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        document.addEventListener('shopify:block:select', this.onShopifyBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onShopifyBlockDeselect);
        if (this.reducedMotion.addEventListener) {
          this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
        } else if (this.reducedMotion.addListener) {
          this.reducedMotion.addListener(this.onReducedMotionChange);
        }
      }

      unbindEvents() {
        this.previousButton?.removeEventListener('click', this.onPreviousClick);
        this.nextButton?.removeEventListener('click', this.onNextClick);
        this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
        this.stage?.removeEventListener('keydown', this.onKeydown);
        this.stage?.removeEventListener('pointerdown', this.onPointerDown);
        this.stage?.removeEventListener('pointerup', this.onPointerUp);
        this.stage?.removeEventListener('pointercancel', this.onPointerCancel);
        this.stage?.removeEventListener('wheel', this.onWheel);
        this.stage?.removeEventListener('click', this.onStageClick, true);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        document.removeEventListener('shopify:block:select', this.onShopifyBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onShopifyBlockDeselect);
        if (this.reducedMotion?.removeEventListener) {
          this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
        } else if (this.reducedMotion?.removeListener) {
          this.reducedMotion.removeListener(this.onReducedMotionChange);
        }
      }

      prepareVideos() {
        this.slides.forEach((slide, index) => {
          const video = this.getVideo(slide);
          if (!video) return;

          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.preload = index === this.currentIndex ? 'metadata' : 'none';
          video.addEventListener('playing', () => {
            if (this.slides[this.currentIndex] === slide) {
              slide.classList.add('has-video-playing');
            }
          });
        });
      }

      previous() {
        this.goTo(this.currentIndex - 1);
      }

      next() {
        this.goTo(this.currentIndex + 1);
      }

      goTo(index, options = {}) {
        this.currentIndex = this.normalizeIndex(index);
        this.update();
        this.playActiveVideo();

        if (options.restartAutoplay !== false) {
          this.restartAutoplay();
        }
      }

      normalizeIndex(index) {
        const total = this.slides.length;
        return ((index % total) + total) % total;
      }

      update() {
        this.updateSlides();
        this.updateDots();
      }

      updateSlides() {
        const total = this.slides.length;

        this.slides.forEach((slide, index) => {
          const state = this.getSlideState(index, total);
          const isActive = state === 'active';
          const links = slide.querySelectorAll('a, button, input, textarea, select');

          slide.dataset.carouselState = state;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          links.forEach((link) => {
            link.tabIndex = isActive ? 0 : -1;
          });

          if (!isActive) {
            slide.classList.remove('has-video-playing');
          }
        });
      }

      getSlideState(index, total) {
        const offset = (index - this.currentIndex + total) % total;

        if (offset === 0) return 'active';
        if (offset === 1) return 'next';
        if (offset === 2) return 'far';
        if (offset === total - 1) return 'previous';

        return 'hidden';
      }

      updateDots() {
        this.dots.forEach((dot, index) => {
          const isActive = index === this.currentIndex;

          dot.classList.toggle('is-active', isActive);
          dot.toggleAttribute('aria-current', isActive);
        });
      }

      playActiveVideo() {
        this.slides.forEach((slide, index) => {
          const video = this.getVideo(slide);
          if (!video) return;

          if (index !== this.currentIndex || document.hidden || this.reducedMotion.matches) {
            video.pause();
            return;
          }

          video.preload = 'metadata';
          video.muted = true;
          video.playsInline = true;

          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
              slide.classList.remove('has-video-playing');
            });
          }
        });
      }

      pauseAllVideos() {
        this.slides?.forEach((slide) => {
          const video = this.getVideo(slide);
          if (video) video.pause();
        });
      }

      getVideo(slide) {
        return slide?.querySelector('video');
      }

      handleDotClick(event) {
        const index = Number(event.currentTarget.dataset.slideIndex);
        if (Number.isNaN(index)) return;

        this.goTo(index);
      }

      handleKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.previous();
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.next();
        }

        if (event.key === 'Home') {
          event.preventDefault();
          this.goTo(0);
        }

        if (event.key === 'End') {
          event.preventDefault();
          this.goTo(this.slides.length - 1);
        }
      }

      handlePointerDown(event) {
        if (event.button !== undefined && event.button !== 0) return;

        this.pointerActive = true;
        this.pointerStartX = event.clientX;
        this.pointerStartY = event.clientY;
      }

      handlePointerUp(event) {
        if (!this.pointerActive) return;

        const deltaX = event.clientX - this.pointerStartX;
        const deltaY = event.clientY - this.pointerStartY;
        const dominantDelta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
        const isSwipe = Math.abs(dominantDelta) > 40;

        this.pointerActive = false;

        if (!isSwipe) return;

        this.suppressClick = true;
        if (dominantDelta < 0) {
          this.next();
        } else {
          this.previous();
        }
      }

      handlePointerCancel() {
        this.pointerActive = false;
      }

      handleWheel(event) {
        if (this.slides.length < 2) return;

        const dominantDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        if (Math.abs(dominantDelta) < 4) return;

        event.preventDefault();
        this.wheelDelta += dominantDelta;

        const now = Date.now();
        const throttleMs = 520;
        const threshold = 50;
        if (now - this.lastWheelAt < throttleMs || Math.abs(this.wheelDelta) < threshold) return;

        if (this.wheelDelta > 0) {
          this.next();
        } else {
          this.previous();
        }

        this.wheelDelta = 0;
        this.lastWheelAt = now;
      }

      handleStageClick(event) {
        if (!this.suppressClick) return;

        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      }

      handleVisibilityChange() {
        if (document.hidden) {
          this.stopAutoplay();
          this.pauseAllVideos();
          return;
        }

        this.playActiveVideo();
        this.startAutoplay();
      }

      handleReducedMotionChange() {
        if (this.reducedMotion.matches) {
          this.stopAutoplay();
          this.pauseAllVideos();
          return;
        }

        this.playActiveVideo();
        this.startAutoplay();
      }

      handleShopifyBlockSelect(event) {
        const slide = this.getEventSlide(event);
        if (!slide) return;

        const index = Number(slide.dataset.slideIndex);
        if (Number.isNaN(index)) return;

        this.pausedByEditor = true;
        this.stopAutoplay();
        this.goTo(index, { restartAutoplay: false });
      }

      handleShopifyBlockDeselect(event) {
        const slide = this.getEventSlide(event);
        if (!slide) return;

        this.pausedByEditor = false;
        this.startAutoplay();
      }

      getEventSlide(event) {
        if (!(event.target instanceof Element)) return null;

        const slide = event.target.closest('[data-hero-video-carousel-slide]');
        return slide && this.contains(slide) ? slide : null;
      }

      startAutoplay() {
        if (!this.shouldAutoplay()) return;

        this.stopAutoplay();
        this.autoplayTimer = window.setInterval(() => this.next(), this.interval);
      }

      stopAutoplay() {
        if (!this.autoplayTimer) return;

        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      restartAutoplay() {
        if (!this.shouldAutoplay()) return;

        this.stopAutoplay();
        this.startAutoplay();
      }

      shouldAutoplay() {
        return (
          this.dataset.autoplay === 'true' &&
          this.slides.length > 1 &&
          !this.reducedMotion.matches &&
          !this.pausedByEditor &&
          !document.hidden
        );
      }
    }
  );
}
